import type { Role } from '../constants/roles';
import { ROLES } from '../constants/roles';

export type HelpLang = 'en' | 'es';

export interface HelpTopic {
  id: string;
  category: string;
  keywords: string[];
  /** Roles that see this FAQ entry (required). */
  roles: Role[];
  en: string;
  es: string;
}

export const HELP_TOPICS: HelpTopic[] = [
  // ── Shared capabilities ──
  {
    id: 'assistant-capabilities',
    category: 'other',
    keywords: ['puedes', 'puedo', 'capabilities', 'ayuda', 'help', 'asistente', 'chatbot', 'qué hace', 'que hace'],
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.RUNNER, ROLES.SUPPORT_AGENT, ROLES.GOD],
    en: 'I explain house operations for your role, open the right screens, help with basic troubleshooting, and only escalate to a live agent or ticket if you confirm.',
    es: 'Explico la operación de tu rol, abro las pantallas correctas, ayudo con fallos básicos y solo escalo a un agente o ticket si lo confirmas.',
  },
  {
    id: 'game-basics',
    category: 'plays',
    keywords: ['fijo', 'corrido', 'parle', 'centena', 'regla', 'rules', 'juego', 'bolita', 'premio', 'payout'],
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.RUNNER],
    en: 'Bolita plays include fijo, corrido, parle, and centena when enabled by the house. Stakes settle after results are published for the window.',
    es: 'Las jugadas incluyen fijo, corrido, parle y centena si la banca las habilita. Los premios se liquidan tras publicar resultados de la ventana.',
  },
  {
    id: 'charada-meanings',
    category: 'plays',
    keywords: [
      'charada', 'sueño', 'dream', 'significado', 'meaning', 'número', 'number',
      'comun', 'americana', 'china', 'santera', 'orisha', 'chiffa',
    ],
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.RUNNER, ROLES.SUPPORT_AGENT, ROLES.GOD],
    en: 'Open Help → Charadas to look up 00–99 in common, American, Chinese, and Santería tables. Search by number or word. Results also explain how fijo, corrido, parle, and centena pay against the triple, and typical Cuba-time publication windows for Miami, Georgia, and New York (feeds may delay).',
    es: 'Abre Ayuda → Charadas para consultar el 00–99 en las tablas común, americana, china y santera. Busca por número o palabra. En Resultados también verás cómo pagan fijo, corrido, parle y centena, y las ventanas típicas de publicación en hora Cuba para Miami, Georgia y Nueva York (la fuente puede retrasarse).',
  },
  // ── Banker ──
  {
    id: 'caps-banker',
    category: 'limits',
    keywords: ['cap', 'caps', 'limit', 'limits', 'tope', 'topes', 'limite', 'límite', '88', 'número'],
    roles: [ROLES.BANKER],
    en: 'Per-number caps stop new stakes once a number hits its house limit. Open Config to review or raise caps, and Users to see who exceeded a cap.',
    es: 'Los topes por número bloquean nuevas jugadas cuando un número alcanza el límite. Entra en Config para revisar o subir topes, y en Usuarios para ver quién lo superó.',
  },
  {
    id: 'cutoff-banker',
    category: 'cutoff',
    keywords: ['cutoff', 'close', 'closes', 'window', 'cierre', 'cerrar', 'ventana', 'hora', 'minutos'],
    roles: [ROLES.BANKER],
    en: 'Cutoff reminders appear on Overview before day and night windows close. Change close times and cutoff minutes in Config.',
    es: 'Configura turno diurno y nocturno (inicio y cierre) en Config. El último minuto antes del cierre no acepta jugadas.',
  },
  {
    id: 'results-banker',
    category: 'results',
    keywords: ['result', 'results', 'publish', 'winner', 'ganador', 'resultado', 'publicar', 'premio', 'youtube', 'video', 'sorteo'],
    roles: [ROLES.BANKER],
    en: 'After the day window closes, publish the three winning numbers from Overview. Listers then see prizes under Results. Florida houses also show the official draw video on each turn in Results (embedded on web; opens YouTube on the mobile app).',
    es: 'Cuando cierra la ventana del día, publica los tres números ganadores desde Resumen. Los listeros verán premios en Resultados. En casas Florida, el video oficial del sorteo queda en cada tirada de Resultados (embebido en la web; abre YouTube en la app).',
  },
  {
    id: 'plays-banker',
    category: 'plays',
    keywords: ['play', 'plays', 'bet', 'stake', 'jugada', 'jugadas', 'apuesta', 'fijo', 'corrido', 'auditar'],
    roles: [ROLES.BANKER],
    en: 'Register plays under New play, and audit the house ledger under Plays. Confirm payouts after results are published.',
    es: 'Registra jugadas en Nueva jugada y audita el libro de la casa en Jugadas. Confirma premios tras publicar resultados.',
  },
  {
    id: 'routes-banker',
    category: 'routes',
    keywords: ['route', 'routes', 'runner', 'stop', 'ruta', 'parada', 'corredor', 'asignar'],
    roles: [ROLES.BANKER],
    en: 'Manage collection stops under Route: edit addresses and expected amounts, skip stops, and review amount mismatches after runner and lister dual confirmation.',
    es: 'Gestiona paradas en Ruta: edita direcciones y montos esperados, salta paradas y revisa discrepancias tras la doble confirmación runner–listador.',
  },
  {
    id: 'users-banker',
    category: 'users',
    keywords: ['user', 'users', 'account', 'password', 'login', 'usuario', 'cuenta', 'contraseña', 'acceso', 'lister', 'runner'],
    roles: [ROLES.BANKER],
    en: 'Manage listers and runners under Users: register staff, set commissions and payment mode, suspend access, review agreements, and trusted devices.',
    es: 'Gestiona listeros y recogedores en Usuarios: registra personal, define comisiones y pago, suspende accesos, revisa acuerdos y dispositivos de confianza.',
  },

  {
    id: 'handoff-lister',
    category: 'routes',
    keywords: ['handoff', 'delivery', 'entrega', 'runner', 'banker', 'confirm', 'monto', 'parada', 'recogida'],
    roles: [ROLES.LISTER],
    en: 'Open Handoff to confirm what you delivered to the runner (both must confirm). If there is no runner, the banker self-collects — you only watch status until collected & secured. Skipped stops need no confirm.',
    es: 'Abre Entrega para confirmar lo que diste al runner (ambos deben confirmar). Si no hay runner, el banker auto-recoge: solo ves el estado hasta recogido y asegurado. Las paradas saltadas no requieren confirmación.',
  },

  // ── Lister ──
  {
    id: 'caps-lister',
    category: 'limits',
    keywords: ['cap', 'caps', 'limit', 'tope', 'topes', 'bloqueado', '88', 'número'],
    roles: [ROLES.LISTER],
    en: 'If New play is blocked, a number may have reached its cap. Note the number and ask your banker to review the limit.',
    es: 'Si Nueva jugada queda bloqueada, un número pudo alcanzar su tope. Anota el número y pide al banquero revisar el límite.',
  },
  {
    id: 'cutoff-lister',
    category: 'cutoff',
    keywords: ['cutoff', 'close', 'window', 'cierre', 'cerrar', 'ventana', 'hora', 'minutos'],
    roles: [ROLES.LISTER],
    en: 'Watch the cutoff countdown on Overview. You cannot inscribe plays after the window closes.',
    es: 'Mira la cuenta regresiva en Resumen. No puedes jugar fuera del turno diurno o nocturno, ni en el último minuto antes del cierre.',
  },
  {
    id: 'plays-lister',
    category: 'plays',
    keywords: ['play', 'plays', 'bet', 'stake', 'jugada', 'jugadas', 'apuesta', 'fijo', 'corrido', 'nueva'],
    roles: [ROLES.LISTER],
    en: 'Record bets from New play: pick type, numbers, and stake. Review everything you submitted today under Plays.',
    es: 'Registra apuestas en Nueva jugada: tipo, números y monto. Revisa lo enviado hoy en Jugadas.',
  },
  {
    id: 'results-lister',
    category: 'results',
    keywords: ['result', 'results', 'winner', 'ganador', 'resultado', 'premio', 'aval', 'youtube', 'video', 'sorteo'],
    roles: [ROLES.LISTER],
    en: 'Official winning numbers, prizes, and the Florida draw video (when available) appear under Results after publication. On the phone app, the video opens in YouTube.',
    es: 'Los números ganadores, tus premios y el video del sorteo de Florida (si está disponible) aparecen en Resultados tras la publicación. En la app, el video se abre en YouTube.',
  },
  {
    id: 'account-lister',
    category: 'users',
    keywords: ['account', 'password', 'login', 'cuenta', 'contraseña', 'acceso', 'entrar', 'google'],
    roles: [ROLES.LISTER],
    en: 'Sign-in issues? Use your registered email, reset your password, or link Google from Settings → Account & security.',
    es: '¿Problemas para entrar? Usa tu correo registrado, restablece la contraseña o vincula Google en Ajustes → Cuenta y seguridad.',
  },

  // ── Runner ──
  {
    id: 'cutoff-runner',
    category: 'cutoff',
    keywords: ['cutoff', 'close', 'window', 'cierre', 'ventana', 'hora'],
    roles: [ROLES.RUNNER],
    en: 'Overview shows today’s collection totals and how the round is progressing before cutoff.',
    es: 'Resumen muestra totales a cobrar hoy y el avance de la ronda antes del cierre.',
  },
  {
    id: 'routes-runner',
    category: 'routes',
    keywords: ['route', 'routes', 'stop', 'stops', 'collect', 'ruta', 'parada', 'cobro', 'recoger', 'mapa'],
    roles: [ROLES.RUNNER],
    en: 'Open Route for today’s stops. Confirm how much you collected before the next stop; the stop clears only after the lister also confirms. Use Navigate for maps, or the map tab when runner_map tiles are configured.',
    es: 'Abre Ruta para las paradas de hoy. Confirma cuánto recogiste antes de la siguiente; la parada solo se cierra cuando el listador también confirma. Usa Navegar para mapas, o la pestaña mapa si hay tiles (runner_map).',
  },
  {
    id: 'history-runner',
    category: 'routes',
    keywords: ['history', 'round', 'rounds', 'historial', 'ronda', 'ayer', 'cobros'],
    roles: [ROLES.RUNNER],
    en: 'History lists completed collection rounds from recent days with totals per stop.',
    es: 'Historial muestra rondas de cobro completadas de días recientes con totales por parada.',
  },
  {
    id: 'account-runner',
    category: 'users',
    keywords: ['account', 'password', 'login', 'cuenta', 'contraseña', 'acceso', 'entrar'],
    roles: [ROLES.RUNNER],
    en: 'Sign-in issues? Use your registered email, reset your password, or link Google from Settings → Account & security.',
    es: '¿Problemas para entrar? Usa tu correo registrado, restablece la contraseña o vincula Google en Ajustes → Cuenta y seguridad.',
  },

  // ── Support agent ──
  {
    id: 'tickets-support',
    category: 'other',
    keywords: ['ticket', 'tickets', 'support', 'soporte', 'resolver', 'escalación'],
    roles: [ROLES.SUPPORT_AGENT],
    en: 'Open Tickets to respond to user escalations. Mark resolved when the issue is closed.',
    es: 'Abre Tickets para responder escalaciones de usuarios. Marca resuelto cuando cierre el caso.',
  },
  {
    id: 'knowledge-support',
    category: 'other',
    keywords: ['knowledge', 'guide', 'guides', 'search', 'guía', 'buscar', 'documento'],
    roles: [ROLES.SUPPORT_AGENT],
    en: 'Knowledge indexes guides and docs. Search there before replying to common questions.',
    es: 'Conocimiento indexa guías y documentos. Busca ahí antes de responder preguntas frecuentes.',
  },
  {
    id: 'audit-support',
    category: 'system',
    keywords: ['audit', 'trace', 'event', 'auditoría', 'evento', 'historial'],
    roles: [ROLES.SUPPORT_AGENT],
    en: 'Audit shows platform events. Filter by action or trace id when investigating a ticket.',
    es: 'Auditoría muestra eventos de la plataforma. Filtra por acción o trace id al investigar un ticket.',
  },

  // ── God ──
  {
    id: 'houses-god',
    category: 'system',
    keywords: ['house', 'houses', 'platform', 'casa', 'casas', 'alcance'],
    roles: [ROLES.GOD],
    en: 'Switch platform scope or a single house from the house selector. Houses lists every workspace.',
    es: 'Cambia alcance de plataforma o una casa desde el selector. Casas lista todos los espacios.',
  },
  {
    id: 'flags-god',
    category: 'system',
    keywords: ['flag', 'flags', 'feature', 'función', 'activar'],
    roles: [ROLES.GOD],
    en: 'Flags toggles platform features per environment. Review before enabling in production.',
    es: 'Flags activa funciones de plataforma por entorno. Revisa antes de habilitar en producción.',
  },
  {
    id: 'tickets-god',
    category: 'other',
    keywords: ['ticket', 'escalation', 'soporte', 'escalación'],
    roles: [ROLES.GOD],
    en: 'Tickets shows escalations from end-user support. Drill into a house scope to see house-specific queues.',
    es: 'Tickets muestra escalaciones de soporte de usuarios. Entra en una casa para ver su cola.',
  },

  // ── Shared (staff who contact support) ──
  {
    id: 'house-broadcast',
    category: 'system',
    keywords: [
      'broadcast', 'aviso', 'casa', 'colaboradores', 'inbox', 'compose',
      'redactar', 'notificación', 'mensaje', 'house',
    ],
    roles: [ROLES.BANKER, ROLES.GOD],
    en: 'From the inbox Compose tab, pick All collaborators (banker) or Everyone in this house (god). That sends an in-app notification to the house — not a 1:1 message.',
    es: 'En la bandeja, pestaña Redactar, elige Todos los colaboradores (banker) o Todos en esta casa (god). Eso envía una notificación in-app a la casa, no un mensaje 1:1.',
  },
  {
    id: 'notifications',
    category: 'system',
    keywords: ['notification', 'notifications', 'alert', 'push', 'sound', 'notificación', 'aviso', 'sonido', 'bandeja'],
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.RUNNER, ROLES.SUPPORT_AGENT, ROLES.GOD],
    en: 'Settings → Notifications controls push, sound, quiet hours, and categories. Only exceptional security alerts interrupt on screen; everything else stays in the inbox (bell).',
    es: 'Ajustes → Notificaciones controla push, sonido, horas silenciosas y categorías. Solo alertas excepcionales de seguridad interrumpen en pantalla; el resto queda en la bandeja (campana).',
  },
  {
    id: 'ticket-enduser',
    category: 'other',
    keywords: ['ticket', 'support', 'help', 'problem', 'bug', 'soporte', 'ayuda', 'problema', 'error'],
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.RUNNER],
    en: 'Use Help chat for guidance. If you still need a person, confirm a live agent or a ticket — tickets are never opened automatically.',
    es: 'Usa el chat de Ayuda para orientarte. Si aún necesitas a alguien, confirma un agente en vivo o un ticket — nunca se abren solos.',
  },
];

export const HELP_GREETING: Record<HelpLang, string> = {
  en: 'Hi! How can I help you today?',
  es: '¡Hola! ¿En qué te ayudo hoy?',
};

/** FAQs visible to the signed-in role only. */
export function getHelpTopicsForRole(role?: string): HelpTopic[] {
  if (!role) return [];
  return HELP_TOPICS.filter((topic) => topic.roles.includes(role as Role));
}

export function getHelpTopicText(topic: HelpTopic, lang: HelpLang): string {
  return topic[lang];
}
